<template>
  <div
    v-if="visible && payload"
    class="billing-overdue-top-alert alert mb-0 rounded-0 border-0 d-flex align-items-center justify-content-center flex-wrap gap-2 py-2 px-3"
    :class="isSevere ? 'bg-danger text-white' : 'alert-warning'"
    role="alert"
  >
    <i
      class="flex-shrink-0"
      :class="isSevere ? 'fas fa-exclamation-triangle' : 'fas fa-exclamation-circle'"
      style="font-size: 1.25rem"
      aria-hidden="true"
    ></i>
    <span class="text-center">
      {{
        $t(bannerMessageKey, {
          days: payload.overdueDays,
          dueDay: payload.paymentDueDayOfMonth || 1,
          daysLeft: daysUntilSuspension
        })
      }}
    </span>
    <router-link
      :to="billingRoute"
      class="fw-bold text-nowrap"
      :class="isSevere ? 'text-white text-decoration-underline' : 'alert-link'"
    >
      {{ $t('Billing overdue top alert link') || 'View billing' }}
    </router-link>
  </div>
</template>

<script>
import { wencesApi } from '@/api/wencesApi';
import Swal from 'sweetalert2';

// Días de mora → mensaje de suspensión y modal de recordatorio
const BILLING_SUSPENSION_OVERDUE_DAYS = 10;
/** Banner rojo + warning when this many (or fewer) days remain until suspension. */
const SEVERE_DAYS_LEFT_THRESHOLD = 3;

export default {
  name: 'BillingOverdueTopAlert',
  data() {
    return {
      payload: null,
      pollTimer: null
    };
  },
  computed: {
    visible() {
      return this.payload && this.payload.show === true && this.payload.overdueDays > 0;
    },
    daysUntilSuspension() {
      const d = this.payload?.overdueDays || 0;
      return Math.max(0, BILLING_SUSPENSION_OVERDUE_DAYS - d);
    },
    isSevere() {
      return this.daysUntilSuspension <= SEVERE_DAYS_LEFT_THRESHOLD;
    },
    bannerMessageKey() {
      const d = this.payload?.overdueDays || 0;
      if (d >= BILLING_SUSPENSION_OVERDUE_DAYS) {
        return 'Billing overdue top alert message suspension';
      }
      return 'Billing overdue top alert message';
    },
    billingRoute() {
      const q = {};
      if (this.$route && this.$route.query && this.$route.query.db) {
        q.db = this.$route.query.db;
      }
      return { path: '/billing', query: Object.keys(q).length ? q : undefined };
    }
  },
  mounted() {
    this.fetchOverdueAlert({ nagOnSuspension: false });
    this.pollTimer = setInterval(
      () => this.fetchOverdueAlert({ nagOnSuspension: false }),
      5 * 60 * 1000
    );
    this._onBillingPaidMonthsUpdated = () =>
      this.fetchOverdueAlert({ nagOnSuspension: false });
    this.$root.$on('billing-paid-months-updated', this._onBillingPaidMonthsUpdated);
  },
  beforeDestroy() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
    this.$root.$off('billing-paid-months-updated', this._onBillingPaidMonthsUpdated);
    this.closeSuspensionSwal();
  },
  watch: {
    '$route.fullPath'() {
      this.fetchOverdueAlert({ nagOnSuspension: true });
    }
  },
  methods: {
    closeSuspensionSwal() {
      if (Swal.isVisible()) {
        Swal.close();
      }
    },
    /** Se muestra en cada cambio de ruta si sigue vencido; no bloquea ni redirige. */
    openSuspensionSwal() {
      Swal.fire({
        icon: 'warning',
        title: this.$tn('Billing suspension nag title'),
        allowOutsideClick: true,
        allowEscapeKey: true,
        showCloseButton: true,
        showCancelButton: true,
        cancelButtonText: this.$tn('Billing suspension swal ok'),
        confirmButtonText: this.$tn('Billing overdue top alert link')
      }).then((result) => {
        if (result.isConfirmed) {
          this.$router.push(this.billingRoute).catch(() => {});
        }
      });
    },
    async fetchOverdueAlert(options = {}) {
      const nagOnSuspension = options.nagOnSuspension === true;
      try {
        const cal = new Date();
        const pad = (n) => String(n).padStart(2, '0');
        const billingAsOfDate = `${cal.getFullYear()}-${pad(cal.getMonth() + 1)}-${pad(cal.getDate())}`;
        const res = await wencesApi.get('/api/settings/billing/overdue-alert', {
          params: {
            calendarYear: cal.getFullYear(),
            calendarMonth: cal.getMonth() + 1,
            billingAsOfDate
          }
        });
        if (res.data?.success && res.data.data) {
          this.payload = res.data.data;
        } else {
          this.payload = { show: false };
        }
      } catch (e) {
        if (e?.response?.status === 401 || e?.response?.status === 403) {
          this.payload = { show: false };
        } else {
          this.payload = { show: false };
        }
      } finally {
        const p = this.payload || { show: false };
        const suspended =
          p.show === true && Number(p.overdueDays) >= BILLING_SUSPENSION_OVERDUE_DAYS;
        this.$nextTick(() => {
          if (!suspended) {
            this.closeSuspensionSwal();
          } else if (nagOnSuspension) {
            this.openSuspensionSwal();
          }
        });
      }
    }
  }
};
</script>

<style scoped>
.billing-overdue-top-alert {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08) !important;
  z-index: 1020;
}
.billing-overdue-top-alert.bg-danger {
  border-bottom-color: rgba(0, 0, 0, 0.12) !important;
}
</style>
